import { useState, useEffect } from "react";
import {
  HiOutlineHeart, HiOutlineCalendarDays, HiOutlineReceiptPercent,
  HiOutlineArrowTopRightOnSquare, HiOutlineSparkles,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./DonationsPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }) : "—";
const fmtMoney = (n, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style:"currency", currency: cur || "USD" }).format(Number(n || 0));

export default function DonationsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalAPI.getDonations()
      .then(res => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const hasGiven = data && data.count > 0;

  return (
    <PortalLayout eyebrow="Giving" pageTitle="Your Donations"
      quote="We rise by lifting others." quoteAuthor="Forge Retreat">

      {loading ? (
        <div className="dn__loading"><div className="dn__spinner" /></div>
      ) : (
        <>
          {/* Totals — only meaningful once they've actually given */}
          {hasGiven && (
            <div className="dn__totals">
              <div className="pc dn__total-card">
                <div className="dn__total-icon"><HiOutlineHeart /></div>
                <div>
                  <p className="dn__total-value">{fmtMoney(data.totalGiven)}</p>
                  <p className="dn__total-label">Given all time</p>
                </div>
              </div>
              <div className="pc dn__total-card">
                <div className="dn__total-icon dn__total-icon--muted"><HiOutlineCalendarDays /></div>
                <div>
                  <p className="dn__total-value">{fmtMoney(data.yearToDate)}</p>
                  <p className="dn__total-label">Given in {data.year}</p>
                </div>
              </div>
            </div>
          )}

          {/* Give again / give first — the public donate flow owns payments */}
          <div className="pc dn__cta">
            <div className="dn__cta-body">
              <p className="dn__cta-title">
                {hasGiven ? "Give again" : "Support the mission"}
              </p>
              <p className="dn__cta-sub">
                Every donation directly funds retreats for veterans, first responders,
                and Gold Star families. Forge Retreat Colombia is a registered 501(c)(3),
                so your gift is tax-deductible.
              </p>
            </div>
            <a href="/donate" className="dn__cta-btn">
              <HiOutlineHeart /> Donate
              <HiOutlineArrowTopRightOnSquare className="dn__cta-btn-ext" />
            </a>
          </div>

          {/* History */}
          {hasGiven ? (
            <div className="pc dn__section">
              <div className="dn__section-head">
                <HiOutlineReceiptPercent className="dn__section-icon" />
                <p className="p-section-title" style={{ margin: 0 }}>Giving History</p>
                <span className="dn__section-count">
                  {data.count} donation{data.count !== 1 ? "s" : ""}
                </span>
              </div>
              <p className="dn__section-sub">
                Keep these receipt numbers for your tax records. Need a formal
                letter? Reach out and we'll send one over.
              </p>

              <div className="dn__list">
                {data.donations.map(d => (
                  <div key={d.id} className="dn__row">
                    <div className="dn__row-main">
                      <p className="dn__row-amount">{fmtMoney(d.amount, d.currency)}</p>
                      <p className="dn__row-meta">
                        {d.campaign || "General Fund"}
                        {d.receipt_number && (
                          <> · <span className="dn__receipt">Receipt {d.receipt_number}</span></>
                        )}
                      </p>
                    </div>
                    <p className="dn__row-date">{fmt(d.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="pc dn__empty">
              <HiOutlineSparkles className="dn__empty-icon" />
              <p className="dn__empty-title">No donations yet</p>
              <p className="dn__empty-sub">
                When you give, your donations and receipts will appear here so you can
                find them easily at tax time.
              </p>
            </div>
          )}
        </>
      )}
    </PortalLayout>
  );
}
